import Button from '@platform-ui/button-latest';
import { TuiIconTdsMediumBonus, TuiIconTdsMediumCheckCirclePositive } from '@tui-react/proprietary-icons';
import cn from 'classnames';
import React from 'react';
import { FormattedMessage as Message } from 'react-intl';
import { PollSchema } from 'schema';
import { ImageWithPreview } from '../../../components/ImageWithPreview';
import Markdown from '../../../components/Markdown';
import { StickyFooterButton } from '../../../components/StickyFooterButton';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { getUrlParam } from '../../../utils/get-url-param';
import { inIframe, isPollsSite, resolveLinkUrl } from '../../../utils/location';
import { transformScores, TypeAwards } from '../../../utils/text';
import { Title } from '../Title';
import { ContentWrapper } from '../Wrappers/ContentWrapper';
import { RootWrapper } from '../Wrappers/RootWrapper';
import styles from './Final.module.css';

type FinalType = PollSchema.FINAL_CLICK_PAGE;

export interface FinalProps<F extends { data: unknown } = FinalType> {
  final: F['data'];
  onFinalClick?: () => void;
  awardType?: TypeAwards;
  points: number;
  maxPoints: number;
  hasAuthQuestion: boolean;
  title: string | null;
}

export const Final: React.FC<FinalProps> = ({
  title,
  onFinalClick,
  final: { text, offer, alignContent, additionalText, image, cta, ctaUrl, footer, background },
  awardType,
  points,
  maxPoints,
  hasAuthQuestion,
}) => {
  const ctaHref = getUrlParam('link1') || resolveLinkUrl(ctaUrl);
  const isInPollsSite = isPollsSite();
  const isPhone = useBreakpoint('phone');

  const isCtaActive = !!cta && !!ctaHref;
  const showFooter = isCtaActive || !!footer;
  const showTitle = title && !inIframe() && !isPhone;

  const buttonSize = isPhone ? 'l' : 'm';

  return (
    <div
      className={cn(styles.container, {
        [c(styles.center)]: alignContent === 'center',
        [c(styles.iframe)]: inIframe(),
      })}
    >
      <RootWrapper className={styles.container}>
        {showTitle && <Title className={styles.title}>{title}</Title>}

        <ContentWrapper>
          <section style={{ gap: 'var(--spacing-l)', display: 'flex', flexDirection: 'column' }}>
            {image?.src ? (
              <div className={styles.image}>
                <ImageWithPreview image={image} background={background} />
              </div>
            ) : !image && !points ? (
              <div className={styles.image}>
                <TuiIconTdsMediumCheckCirclePositive iconSize="var(--icon-l)" />
              </div>
            ) : !image && !!points ? (
              <div className={styles.image}>
                <TuiIconTdsMediumBonus theme="green" iconSize="var(--icon-l)" />
              </div>
            ) : null}

            <div className={cn(styles.content, { [c(styles.withFooter)]: showFooter })}>
              {text && (
                <Markdown className={styles.text}>{transformScores(awardType, text, points, maxPoints)}</Markdown>
              )}

              {offer && <Markdown className={styles.offer}>{offer}</Markdown>}

              {additionalText && <Markdown className={styles.additionalText}>{additionalText}</Markdown>}
            </div>
          </section>
        </ContentWrapper>

        {showFooter && (
          <StickyFooterButton>
            <footer className={styles.footer}>
              {isCtaActive && (
                <span className={styles.ctaButton}>
                  <Button
                    data-cta-button // Нужен, чтобы исключить из трекинга quiz-link
                    onClick={onFinalClick}
                    href={ctaHref}
                    target="_blank"
                    size={buttonSize}
                    wide={isPhone}
                  >
                    {cta}
                  </Button>
                </span>
              )}

              {footer && <Markdown className={styles.footerText}>{footer}</Markdown>}
            </footer>
          </StickyFooterButton>
        )}
      </RootWrapper>

      {isInPollsSite && hasAuthQuestion && (
        <span className={styles.madeInPolls}>
          <Message id={'System.MadeInPolls'} />{' '}
          <a rel={'noreferrer nooperer'} target={'_blank'} href={'https://polls-admin.tcsbank.ru/surveys'}>
            Polls
          </a>
        </span>
      )}
    </div>
  );
};
